/**
 * Unified post-auth routing for guest analysis → coaching upgrade flows.
 * Explore and analyzer each persist pending coaching in localStorage.
 */

import {
  hasExploreResumePending,
  getExploreResumePath,
  EXPLORE_SESSION_KEY,
} from '@/message-analyzer/lib/explore-session'
import {
  hasAnalyzerResumePending,
  getAnalyzerResumePath,
  ANALYZER_GUEST_SESSION_KEY,
} from '@/message-analyzer/lib/analyzer-guest-session'

/** localStorage keys preserved across forced sign-out when guest work is in progress */
export const GUEST_SESSION_LOCAL_STORAGE_KEYS = [
  EXPLORE_SESSION_KEY,
  ANALYZER_GUEST_SESSION_KEY,
] as const

/** sessionStorage keys preserved when guest analysis results must survive auth recovery */
export const GUEST_SESSION_SESSION_STORAGE_KEYS = ['analyzer_session_state'] as const

export function hasGuestCoachingResumePending(): boolean {
  return hasExploreResumePending() || hasAnalyzerResumePending()
}

/** Prefer explore, then analyzer, when coaching is waiting after auth. */
export function resolvePostAuthDestination(fallback = '/'): string {
  if (hasExploreResumePending()) return getExploreResumePath()
  if (hasAnalyzerResumePending()) return getAnalyzerResumePath()
  return fallback
}

export function snapshotGuestLocalStorage(): Record<string, string> {
  const preserved: Record<string, string> = {}
  if (typeof window === 'undefined') return preserved
  for (const key of GUEST_SESSION_LOCAL_STORAGE_KEYS) {
    const value = localStorage.getItem(key)
    if (value) preserved[key] = value
  }
  return preserved
}

export function snapshotGuestSessionStorage(): Record<string, string> {
  const preserved: Record<string, string> = {}
  if (typeof window === 'undefined') return preserved
  for (const key of GUEST_SESSION_SESSION_STORAGE_KEYS) {
    const value = sessionStorage.getItem(key)
    if (value) preserved[key] = value
  }
  return preserved
}

export function restoreGuestStorageSnapshots(
  local: Record<string, string>,
  session: Record<string, string>,
): void {
  if (typeof window === 'undefined') return
  for (const [key, value] of Object.entries(local)) {
    localStorage.setItem(key, value)
  }
  for (const [key, value] of Object.entries(session)) {
    sessionStorage.setItem(key, value)
  }
}
